import { useCallback, useEffect, useLayoutEffect, useState } from 'react'

export type ScrollEdges = {
	/** Flush against the left end — nothing is hidden behind a left-pinned column. */
	start: boolean
	/** Flush against the right end — nothing is hidden behind a right-pinned column. */
	end: boolean
}

/**
 * Tracks whether a horizontal scroller is flush against each of its ends, which is
 * what tells a frozen column whether it is currently covering anything.
 *
 * `resolve` is a getter rather than a ref because the scroller is not always the
 * element the caller renders: the nested sub-table scrolls inside the parent
 * grid's viewport and reaches it with `closest()`.
 *
 * A grid can change width with no scroll event at all — rows arriving, a column
 * toggling, the sidebar collapsing — so this re-measures on `deps` and on a
 * `ResizeObserver`, not only on scroll.
 */
export const useScrollEdges = (
	resolve: () => HTMLElement | null,
	deps: unknown[] = []
) => {
	const [edge, setEdge] = useState<ScrollEdges>({ start: true, end: true })

	const measure = useCallback(() => {
		const el = resolve()
		if (!el) return
		const start = el.scrollLeft <= 0
		const end = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1
		setEdge((prev) =>
			prev.start === start && prev.end === end ? prev : { start, end }
		)
		// `resolve` is re-created every render by design (it closes over a ref), so
		// it is deliberately not a dependency — its identity would otherwise thrash
		// the listener effect below on every render.
	}, [])

	useLayoutEffect(() => {
		measure()
	}, [measure, ...deps])

	useEffect(() => {
		const el = resolve()
		if (!el) return
		el.addEventListener('scroll', measure, { passive: true })
		const ro =
			typeof ResizeObserver !== 'undefined'
				? new ResizeObserver(() => measure())
				: null
		ro?.observe(el)
		return () => {
			el.removeEventListener('scroll', measure)
			ro?.disconnect()
		}
	}, [measure, ...deps])

	return edge
}
